/**
 * Page publique de vérification d'une réception SADAGAS.
 *
 * Ouverte par scan du QR code de l'étiquette imprimée → URL `/v/{RCP-...}`.
 * Aucune auth requise. Affiche les infos non-sensibles pour authentifier
 * visuellement que l'étiquette est bien valide (anti-fraude basique).
 *
 * Mobile-first : carte unique, larges polices, pas de scroll horizontal.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyValidationPage = ({ validationNumber, token }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const path = token ? `${validationNumber}/${token}` : validationNumber;
        const r = await axios.get(`${API}/receptions/public/verify/${path}`);
        if (alive) setState({ loading: false, data: r.data, error: null });
      } catch (e) {
        if (alive) {
          setState({
            loading: false,
            data: null,
            error: e?.response?.data?.detail || 'Réception introuvable',
          });
        }
      }
    })();
    return () => { alive = false; };
  }, [validationNumber, token]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-3" />
          <p className="text-zinc-600">Vérification en cours…</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border-2 border-red-200 p-6 text-center" data-testid="verify-error-card">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-red-700 mb-2">Étiquette non reconnue</h1>
          <p className="text-zinc-600 mb-4">{state.error}</p>
          <p className="font-mono text-sm text-zinc-500 bg-zinc-50 p-2 rounded">{validationNumber}</p>
          <p className="text-xs text-zinc-400 mt-4">
            Si vous pensez qu'il s'agit d'une erreur, contactez la Pharmacie Saint Ange Des Amis.
          </p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const dateStr = d.validated_at
    ? new Date(d.validated_at).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white p-4 sm:p-8">
      <div className="max-w-md mx-auto">
        {/* Header pharmacie */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-3">
            <ShieldCheck className="w-8 h-8 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-bold text-emerald-900">{d.pharmacy_name}</h1>
          <p className="text-sm text-emerald-700">Vérification d'authenticité</p>
        </div>

        {/* Carte de vérification */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-emerald-200 overflow-hidden" data-testid="verify-success-card">
          {/* Bandeau succès */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-5 py-4 flex items-center gap-3">
            <CheckCircle2 className="w-7 h-7 flex-shrink-0" />
            <div>
              <p className="font-bold text-lg leading-tight">Réception authentifiée</p>
              <p className="text-emerald-50 text-sm">Cette étiquette est valide</p>
            </div>
          </div>

          {/* Numéro RCP en gros */}
          <div className="px-5 py-4 bg-emerald-50 border-b border-emerald-100">
            <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
              Numéro de validation
            </p>
            <p
              className="font-mono font-bold text-2xl sm:text-3xl text-emerald-900 tracking-wider break-all"
              data-testid="verify-validation-number"
            >
              {d.validation_number}
            </p>
          </div>

          {/* Détails — affichage type "fiche" mobile-friendly */}
          <div className="divide-y divide-zinc-100">
            <Field label="Fournisseur" value={d.fournisseur} />
            <Field label="N° Bon de Livraison" value={d.bl_number} mono />
            {d.bl_date && <Field label="Date BL" value={d.bl_date} />}
            <Field label="Vérifié par" value={d.submitted_by_name} />
            <Field label="Validé par" value={d.validated_by_name} />
            <Field label="Date de validation" value={dateStr} />
            {typeof d.lines_total === 'number' && (
              <Field
                label="Lignes"
                value={`${d.lines_accepted}/${d.lines_total} conformes${
                  d.anomalies?.length ? ` · ${d.anomalies.length} anomalie(s)` : ''
                }`}
              />
            )}
          </div>

          {/* Anomalies détectées (s'il y en a) */}
          {d.anomalies?.length > 0 && (
            <div className="border-t border-amber-200 bg-amber-50/60" data-testid="verify-anomalies-block">
              <div className="px-5 py-2.5 bg-amber-100 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-600" />
                <p className="text-xs font-bold uppercase tracking-wider text-amber-900">
                  Anomalies signalées ({d.anomalies.length})
                </p>
              </div>
              <ul className="divide-y divide-amber-100">
                {d.anomalies.map((a, i) => (
                  <li key={i} className="px-5 py-3 text-sm" data-testid={`verify-anomaly-${i}`}>
                    <p className="font-medium text-zinc-900 leading-tight">{a.product}</p>
                    <p className="text-xs text-amber-800 mt-0.5">
                      {a.reason}
                      {a.quantity_bl != null && a.quantity_received != null && (
                        <span className="ml-1.5 font-mono text-zinc-600">
                          (BL : {a.quantity_bl} · Reçu : {a.quantity_received})
                        </span>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 text-center">
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Cette page certifie qu'une réception sous ce numéro a bien été
              <strong> validée par deux personnes différentes</strong> dans le système
              SADAGAS de la Pharmacie Saint Ange Des Amis.
            </p>
          </div>
        </div>

        {/* Info anti-fraude */}
        <p className="text-xs text-zinc-500 text-center mt-5 leading-relaxed">
          🔒 Si l'étiquette d'un BL physique ne correspond pas à cette page,
          ou si la page indique « Étiquette non reconnue », alertez immédiatement
          la pharmacie.
        </p>
      </div>
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div className="px-5 py-3 flex justify-between items-start gap-3">
    <span className="text-xs uppercase tracking-wide text-zinc-500 font-semibold flex-shrink-0">
      {label}
    </span>
    <span className={`text-sm text-zinc-900 text-right ${mono ? 'font-mono' : 'font-medium'}`}>
      {value}
    </span>
  </div>
);

export default VerifyValidationPage;
